import { useState } from "react";
import { ALL, NONE } from "@miplatform/constants";
import { AccountType } from "@miplatform/types";
import {
  useAccounts,
  useCommunities,
  useDepartments,
  useSeries,
  useSeriesGroup,
} from "@mibuildweb/hooks";
import { BID_STATUS_DISPLAY } from "../constants/constants";
import { BidFilter } from "../types";
import { useBidSearchParams } from "./useBidSearchParams";

const initialFilters: BidFilter = {
  groupBy: NONE,
  sortBy: "modifiedOn desc",
  builderId: ALL,
  communityId: ALL,
  seriesGroupId: ALL,
  seriesId: ALL,
  assigneeId: ALL,
  planStatus: ALL,
};

export const useBidFilters = () => {
  const { getFiltersFromSearchParams, searchParams, setSearchParams } =
    useBidSearchParams();

  const [filters, setFilters] = useState<BidFilter>(
    getFiltersFromSearchParams() || initialFilters
  );

  const {
    isLoading: isAccountsLoading,
    accounts,
    getAccounts,
  } = useAccounts({
    accountTypes: [AccountType.Builder],
  });
  const {
    isLoading: isSeriesGroupsLoading,
    seriesGroups,
    getSeriesGroups,
  } = useSeriesGroup();
  const { isLoading: isSeriesLoading, series, getSeries } = useSeries();
  const {
    isLoading: isCommunitiesLoading,
    communities,
    getCommunities,
  } = useCommunities();
  const {
    loading: isDepartmentsLoading,
    departments,
    getDepartments,
  } = useDepartments();

  const getFilterData = async () => {
    await Promise.all([
      getCommunities(),
      getAccounts(),
      getDepartments({ filters: { name: "Estimating" } }),
      getSeriesGroups(),
      getSeries(),
    ]);
  };

  const handleFilterApply = (params: BidFilter) => {
    setFilters(params);
  };

  const resetFilters = () => {
    setFilters((prev) => ({ ...prev, ...initialFilters }));
  };

  const builderOptions = [
    { label: "All", value: ALL },
    ...accounts.map((a) => ({ label: a.name, value: a.id })),
  ];

  const communityOptions = [
    { label: "All", value: ALL },
    ...communities.map((c) => ({ label: c.name, value: c.id })),
  ];

  const seriesGroupOptions = [
    { label: "All", value: ALL },
    ...seriesGroups.map((sg) => ({ label: sg.name, value: sg.id })),
  ];

  const seriesOptions = [
    { label: "All", value: ALL },
    ...series.map((s) => ({ label: s.name, value: s.id })),
  ];

  const assigneeOptions = [
    { label: "All", value: ALL },
    ...departments
      .flatMap((d) => d.users)
      .map((u) => ({ label: `${u.firstName} ${u.lastName}`, value: u.id })),
  ];

  const planStatusOptions = [
    { label: "All", value: ALL },
    { label: BID_STATUS_DISPLAY.ASSIGNMENT, value: "Assignment" },
    { label: BID_STATUS_DISPLAY.SETUP, value: "Setup" },
    { label: BID_STATUS_DISPLAY.PRICING, value: "Pricing" },
    { label: BID_STATUS_DISPLAY.APPROVED, value: "Approved" },
  ];

  return {
    filters,
    setFilters,
    handleFilterApply,
    resetFilters,
    accounts,
    communities,
    seriesGroups,
    series,
    departments,
    builderOptions,
    communityOptions,
    seriesGroupOptions,
    seriesOptions,
    assigneeOptions,
    planStatusOptions,
    getFiltersFromSearchParams,
    getFilterData,
    searchParams,
    setSearchParams,
    isLoading:
      isAccountsLoading ||
      isSeriesGroupsLoading ||
      isSeriesLoading ||
      isCommunitiesLoading ||
      isDepartmentsLoading,
  };
};
